import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Button } from '@/ui/components/atoms/Button'
import { ExpertiseList } from '@/ui/components/molecules/ExpertiseList'
import { Affaire } from '../../types/search.types'
import { mapAffaireToExpertiseCard, mapAffairesToExpertiseCards } from '../../utils'
import './DossierSearch.styles.css'

interface DossierSearchProps {
  affaire: Affaire | null
  affaires: Affaire[]
  matchType: 'dossier_id' | 'address' | null
  loading: boolean
  error: string | null
  searchQuery: string
  onSearch: (query: string) => void
  onSetSearchQuery: (query: string) => void
}

export const DossierSearch: React.FC<DossierSearchProps> = ({
  affaire,
  affaires,
  matchType,
  loading,
  error,
  searchQuery,
  onSearch,
  onSetSearchQuery
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const basePath = location.pathname.includes('/bureau') ? '/bureau' : '/admin'

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSearch(searchQuery)
  }

  const navigateToAffaire = (affaireData: Affaire) => {
    navigate(`${basePath}/affaire/${affaireData.id_unique}`, {
      state: {
        from: `${basePath}/search`,
        searchContext: {
          tab: 'dossier',
          searchQuery: searchQuery
        }
      }
    })
  }

  const handleFileMakerClick = (affaireId: string) => {
    // Open FileMaker link - adjust URL based on your FileMaker setup
    const fmUrl = `fmp://$/myexpert?script=OpenAffaire&param=${affaireId}`
    window.location.href = fmUrl
  }

  return (
    <>
      <div className="search-input-section">
        <form onSubmit={handleSubmit} className="search-form">
          <div className="search-input-wrapper">
            <svg className="search-input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
            </svg>
            <input
              id="dossier-search"
              type="text"
              className="search-input-field"
              placeholder="Entrez un ID affaire (5 chiffres) ou une adresse (rue, ville, code postal)"
              value={searchQuery}
              onChange={(e) => onSetSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                className="search-input-clear"
                onClick={() => onSetSearchQuery('')}
                aria-label="Clear search"
              >
                <XMarkIcon className="search-clear-icon" />
              </button>
            )}
          </div>
          <Button
            type="submit"
            variant="primary"
            size="medium"
            disabled={loading || !searchQuery.trim()}
          >
            {loading ? 'Recherche...' : 'Rechercher'}
          </Button>
        </form>
      </div>

      {loading && (
        <div className="search-results-section">
          <div className="search-loading-state">
            <div className="search-loading-spinner"></div>
            <p className="search-loading-text">Recherche de l'affaire...</p>
          </div>
        </div>
      )}

      {error && (
        <div className="search-error-state">
          <p className="search-error-message">⚠️ {error}</p>
        </div>
      )}

      {affaire && (
        <div className="search-results-section">
          <div className="search-results-header">
            <h2 className="search-results-title">Affaire trouvée</h2>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {matchType === 'dossier_id' && (
                <span className="affaire-badge affaire-badge--primary">
                  ID Match
                </span>
              )}
              <span className="search-results-count">ID: {affaire.id_unique}</span>
            </div>
          </div>

          <div className="search-results-body">
            <ExpertiseList
              expertises={[mapAffaireToExpertiseCard(affaire)]}
              onExpertiseClick={() => navigateToAffaire(affaire)}
              onFileMakerClick={() => handleFileMakerClick(affaire.id_unique)}
              showFacadeImage={true}
              showProgress={true}
              showActions={true}
              showUrgency={false}
              showFileMaker={true}
            />
          </div>
        </div>
      )}

      {affaires.length > 0 && (
        <div className="search-results-section">
          <div className="search-results-header">
            <h2 className="search-results-title">Affaires trouvées par adresse</h2>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <span className="affaire-badge affaire-badge--warning">
                Address Match
              </span>
              <span className="search-results-count">{affaires.length} affaire{affaires.length > 1 ? 's' : ''}</span>
            </div>
          </div>

          <div className="search-results-body">
            <ExpertiseList
              expertises={mapAffairesToExpertiseCards(affaires)}
              onExpertiseClick={(expertise) => {
                const aff = affaires.find(a => a.id_unique === expertise.id)
                if (aff) navigateToAffaire(aff)
              }}
              onFileMakerClick={(expertise) => {
                handleFileMakerClick(expertise.id)
              }}
              showFacadeImage={true}
              showProgress={true}
              showActions={true}
              showUrgency={false}
              showFileMaker={true}
            />
          </div>
        </div>
      )}

      {!affaire && !affaires.length && !error && !loading && !searchQuery && (
        <div className="search-results-section">
          <div className="search-empty-state">
            <div className="search-empty-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
              </svg>
            </div>
            <h3 className="search-empty-title">Recherche d'affaire</h3>
            <p className="search-empty-description">
              Entrez un ID affaire (5 chiffres) ou une adresse (rue, ville, code postal) pour rechercher
            </p>
          </div>
        </div>
      )}

      {!affaire && !affaires.length && !error && !loading && searchQuery && (
        <div className="search-results-section">
          <div className="search-empty-state">
            <div className="search-empty-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
                <path d="M8 11h6"/>
              </svg>
            </div>
            <h3 className="search-empty-title">Aucune affaire trouvée</h3>
            <p className="search-empty-description">
              Aucune affaire ne correspond à "{searchQuery}"
            </p>
          </div>
        </div>
      )}
    </>
  )
}
